import { type Address, erc20Abi } from 'viem'
import { type UseReadContractParameters, useReadContract } from 'wagmi'

type BalanceQueryOptions = NonNullable<
  UseReadContractParameters<typeof erc20Abi, 'balanceOf'>['query']
>

/** Cache options a caller may tune. The hook owns `enabled`, the query key and the fetch. */
type TokenBalanceQueryOptions = Pick<BalanceQueryOptions, 'staleTime' | 'gcTime'>

interface UseTokenBalanceParameters {
  chainId: number | undefined
  userAddress?: Address
  tokenAddress: Address | undefined
  /** Extra gate on top of the inputs, for callers whose surface is inactive. */
  enabled?: boolean
  /** Poll interval in ms. Omit to read once — a wallet balance moved elsewhere then stays stale. */
  refetchInterval?: number | false
  query?: TokenBalanceQueryOptions
}

/**
 * An incomplete target reads nothing rather than falling back to a default chain or token, which
 * would return a plausible balance for the wrong one. The target is withheld from the read itself,
 * not just from `query.enabled`: wagmi substitutes the connected chain for an absent `chainId`, so
 * a gate that left `address` in place would key the query against that fallback chain and hand
 * back its cached balance — or fetch it outright on `refetch()`.
 */
export function useTokenBalance({
  chainId,
  userAddress,
  tokenAddress,
  enabled = true,
  refetchInterval,
  query
}: UseTokenBalanceParameters) {
  const hasTarget = !!chainId && !!userAddress && !!tokenAddress
  const isEnabled = enabled && hasTarget

  return useReadContract({
    chainId,
    address: hasTarget ? tokenAddress : undefined,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: userAddress ? [userAddress] : undefined,
    query: {
      ...query,
      enabled: isEnabled,
      refetchInterval: isEnabled ? refetchInterval : undefined
    }
  })
}
